// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, type ReactNode, useEffect } from 'react';

import { type EntityAttachment, type EntityAttachmentType, formatDate } from '@evolvemep/api-ts';
import { Card, Flex, ActionIcon, Text, type MantineColor, Loader, type CardProps, Stack } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { useFileCache } from 'components/Mantine/FileUploads/useFileCache';
import { TextWithTruncate } from 'components/Mantine/TextWithTruncate';
import { withHttp } from 'helpers/common';
import { firstNonEmpty } from 'helpers/firstNonEmpty';
import { isKeyOf } from 'helpers/isKeyOf';
import { useUsersInfo } from 'hooks/user/useUsersInfo';

type Props<T extends EntityAttachmentType> = {
  attachment: EntityAttachment<T>;
  entityType: T;
  rowIndex: number;
  onAttachmentSelected: ((attachment: EntityAttachment<never>, rowIndex: number, section: T) => void) | undefined;
  onDeleteAttachment: ((attachment: EntityAttachment<T>) => void) | null;
  selected?: boolean;
} & Pick<CardProps, 'miw' | 'maw' | 'flex'>;

const LinkFollower = <T extends EntityAttachmentType>({
  attachment,
  rowIndex,
  entityType,
  onAttachmentSelected,
}: {
  attachment: EntityAttachment<T>;
  rowIndex: number;
  entityType: T;
  onAttachmentSelected: ((attachment: EntityAttachment<T>, rowIndex: number, section: T) => void) | undefined;
}) => {
  const { downloadFile } = useFileCache();
  const [downloading, setDownloading] = useState(false);
  const isFile = 'file' in attachment;
  const isLink = 'webLink' in attachment;
  return (
    <Flex
      component={isLink ? 'a' : undefined}
      href={isLink ? withHttp(attachment.webLink.url) : undefined}
      target="_blank"
      onClick={() => {
        if (!isFile) return;
        if (onAttachmentSelected) {
          onAttachmentSelected(attachment, rowIndex, entityType);
          return;
        }
        setDownloading(true);
        void downloadFile(attachment.file.fileId).finally(() => setDownloading(false));
      }}
      align="center"
      gap="xs"
      className="attachment-link link"
      style={{ overflow: 'hidden' }}
    >
      <TextWithTruncate>
        {isFile ? attachment.file.fileName : firstNonEmpty(attachment.webLink.name, attachment.webLink.url)}
      </TextWithTruncate>

      {!onAttachmentSelected &&
        (downloading ? (
          <Loader size="xs" />
        ) : (
          <EvolveIcon className="hover-title--on" size="sm" icon={isFile ? 'Download' : 'FollowLink'} />
        ))}
    </Flex>
  );
};

const backgrounds = {
  pdf: 'red',
  png: 'blue',
  jpg: 'orange',
  jpeg: 'orange',
  xlsx: 'green',
} as const satisfies Record<string, MantineColor>;

export const FileCard = ({
  selected,
  icon,
  iconBg,
  children,
  subtitle,
  rightSection,
  ...props
}: {
  icon: ReactNode | null;
  iconBg?: MantineColor;
  selected?: boolean;
  rightSection?: ReactNode;
  subtitle?: ReactNode;
} & CardProps) => (
  <Card
    withBorder
    p="xs"
    py={6}
    mih={54}
    className="hover-title"
    bg={selected ? 'var(--mantine-primary-color-0)' : 'white'}
    {...props}
  >
    <Flex align="center" justify="space-between" h="100%" gap="xs">
      <Flex align="center" gap="md" fz="sm" style={{ overflow: 'hidden' }}>
        {!!icon && (
          <Card
            p={0}
            w={32}
            h={32}
            miw={32}
            bg={iconBg}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Card>
        )}
        <Stack gap={0}>
          {children}
          {!!subtitle && (
            <Flex c="dimmed" fz="xs" align="center">
              {subtitle}
            </Flex>
          )}
        </Stack>
      </Flex>
      {rightSection}
    </Flex>
  </Card>
);

export const AttachmentCard = <T extends EntityAttachmentType>({
  attachment,
  entityType,
  rowIndex,
  onDeleteAttachment,
  onAttachmentSelected,
  selected,
  ...props
}: Props<T>) => {
  const { fetchUserInfo, userInfo } = useUsersInfo();
  const isFile = 'file' in attachment;
  const extension = isFile ? attachment.file.fileName.split('.').pop() : '';

  useEffect(() => {
    void fetchUserInfo(attachment.createdBy);
  }, [attachment.createdBy, fetchUserInfo]);

  const verb = isFile ? 'Uploaded' : 'Added';

  return (
    <FileCard
      key={attachment.rowId}
      selected={selected}
      icon={
        isFile ? (
          <Text fz="xs" c="white" style={{ textWrap: 'nowrap' }}>
            .{extension?.slice(0, 4)}
          </Text>
        ) : (
          <EvolveIcon icon="External" color="white" />
        )
      }
      iconBg={isFile ? (isKeyOf(extension, backgrounds) ? backgrounds[extension] : 'gray') : 'teal'}
      rightSection={
        !!onDeleteAttachment && (
          <ActionIcon onClick={() => onDeleteAttachment(attachment)} variant="transparent" color="red">
            <EvolveIcon icon="Delete" className="hover-title--on" size="sm" />
          </ActionIcon>
        )
      }
      subtitle={
        <AgGridStyleTooltip
          label={
            <Stack gap={0}>
              <Text>
                {verb} by {userInfo[attachment.createdBy]?.completeName ?? 'unknown'}
              </Text>
              <Text>{formatDate(attachment.createdOn, 'PP p')}</Text>
            </Stack>
          }
          withArrow
        >
          <Text>
            {verb} on {formatDate(attachment.createdOn, 'MM/dd/yyyy')}
          </Text>
        </AgGridStyleTooltip>
      }
      {...props}
    >
      <LinkFollower
        attachment={attachment}
        entityType={entityType}
        rowIndex={rowIndex}
        onAttachmentSelected={onAttachmentSelected}
      />
    </FileCard>
  );
};
